import {
  type EmailSignaturesActions,
  initialEmailSignatureContextValues,
} from '@reducers/email-signature-reducer';
import { useCallback } from 'react';

import { EmailSignatureFieldTypes } from '../../../consts';
// types
import type { EmailSignatureEnvironmentalFooterId, EmailSignatureV2 } from '../../../types';
// utils
import type EmailSignatureImagesUploader from '../../EmailSignatureImagesUploader';
// hooks and context
import useEnvironmentalFooterIconGenerator from '../../useEnvironmentalFooterIconGenerator';
import useInitialEmailSignature from '../../useInitialEmailSignature';
import { useEnvironmentalIconGeneratorSwitch } from '../useIconGeneratorSwitches';

type EnvironmentalFooterServicesProps = {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
};

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

function useEnvironmentalFooterServices({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,
}: EnvironmentalFooterServicesProps): void {
  const { initialEmailSignature, initialEmailSignatureHasBeenLoaded } = useInitialEmailSignature(
    emailSignature,
    emailSignatureHasBeenLoaded,
  );

  const handleUpdateNewLocalEnvironmentalIconUrl = useCallback(
    (iconImageUrl: string, environmentalFooterId: EmailSignatureEnvironmentalFooterId) => {
      emailSignatureDispatch({
        type: 'UPDATE_ENVIRONMENTAL_FOOTER_ICON_URL',
        value: iconImageUrl,
      });

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          environmentalFooterId,
          iconImageUrl,
          EmailSignatureFieldTypes.ENVIRONMENTAL_FOOTER,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_ENVIRONMENTAL_FOOTER',
            });
          },
        );
      });
    },
    [emailSignatureDispatch, uploadEmailSignatureFieldImagesThunk, handleOnErrorUploading],
  );

  const turnOnEnvironmentalFooterIconGenerator = useEnvironmentalIconGeneratorSwitch(
    emailSignatureHasBeenLoaded,
    initialEmailSignatureHasBeenLoaded,
    initialEmailSignature || initialEmailSignatureContextValues,
    emailSignature,
  );

  useEnvironmentalFooterIconGenerator(
    emailSignature,
    handleUpdateNewLocalEnvironmentalIconUrl,
    turnOnEnvironmentalFooterIconGenerator,
  );
}

export { useEnvironmentalFooterServices };
